import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges
} from '@angular/core';
import { Observable, combineLatest, BehaviorSubject, Subscription } from 'rxjs';
import { delay, map, pluck, distinctUntilChanged } from 'rxjs/operators';

import { IVbrickPlayerConfig, ReactionCfg, VideogularComponent, VBrickPlayerComponent } from '@vbrick/vbrick-player';

import { AnalyticsViewContext } from 'rev-shared/analytics/Constant';
import { VideoOverlay, VIDEO_OVERLAY_TYPE } from 'rev-shared/videoPlayer/VideoOverlay';
import { isIe, isMobileSafari } from 'rev-shared/util/UserAgentUtil';
import { refreshViewerIdOverlay } from 'rev-shared/videoPlayer/VideoOverlayUtility';

import { WebcastModel } from 'rev-portal/scheduledEvents/webcast/model/WebcastModel';

import styles from './VbWebcastVideo.Component.module.less';

@Component({
	selector: 'vb-webcast-video',
	templateUrl: './VbWebcastVideo.Component.html',
	host: {
		class: 'box-block'
	},
	standalone: false
})
export class VbWebcastVideoComponent implements OnChanges, OnDestroy {
	@Input() public viewContext: AnalyticsViewContext;
	@Input() public webcast: WebcastModel;
	@Input() public isStopped: boolean;
	@Input() public fillVideo: boolean;
	@Output() public onPlayerReady: EventEmitter<{ vgAPI: VideogularComponent, player: VBrickPlayerComponent }> = new EventEmitter<any>();

	private isInitialized: boolean;
	private vgAPI: VideogularComponent;
	private readonly playerSizeSubject$: BehaviorSubject<{ height: number; width: number }> = new BehaviorSubject<{ height: number; width: number }>(undefined);
	private readonly subscription: Subscription = new Subscription();
	private prevPlayerSize: {
		width: number;
		height: number;
	};

	public readonly styles = styles;
	public disposePlayer: boolean;
	public height: number;
	public width: number;
	public playerConfig: IVbrickPlayerConfig;
	public viewerIdOverlayChanged$: Observable<VideoOverlay>;
	public videoContainerSizeChanged$: Observable<{ height: number; width: number }>;

	public isPlayerContext$: Observable<any>;
	public reactionCfg: ReactionCfg;

	public browser = {
		msie: isIe(), //clean up later if not using anywhere.
		isIosSafari: isMobileSafari()
	};

	constructor(
		private readonly cdr: ChangeDetectorRef,
		private readonly elementRef: ElementRef
	) { }

	public ngOnInit(): void {
		setTimeout(() => this.isInitialized = true, 1000);
		this.isPlayerContext$ = this.webcast.webcast$
			.pipe(
				map((webcast: WebcastModel) => {
					return {
						isVodPreview: webcast.isVodSource && !webcast.webcastStatus.isBroadcasting
					};
				}),
				delay(500)
			);

		this.playerConfig = {
			playerControls: {
				//hide dual button for producer as it will play only speaker all the time.
				dualLayoutControl: this.webcast.dualPlaybackSource && !this.webcast.isProducerEvent
			}
		};

		this.subscribeViewerIdOverlay();

		this.subscription.add(this.webcast.getReactionCfg().subscribe({
			next: reactionCfg => this.reactionCfg = reactionCfg,
			error: e => console.error('Error getting reaction config', e)
		}));
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if(changes.isStopped) {
			//In case of native playback, if video element is not removed from dom..It keeps fetching playlist
			// and show old video after broadcast enabled.
			this.disposePlayer = this.browser.isIosSafari && this.isStopped;

			//iOS autoplay not working as expected. It's making the video freeze.
			if (this.vgAPI && !this.browser.isIosSafari) {
				if(!this.isStopped &&
					this.isInitialized &&
					this.webcast.webcastStatus.hasBroadcasted) {
					setTimeout(() => this.vgAPI.play(), 200); //need a timeout to trigger a play after player is settled.
				}
				else {
					this.vgAPI.stop();
				}
			}
		}
	}

	public ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	public onPlayerReadyInternal(event: { vgAPI: VideogularComponent, player: VBrickPlayerComponent }): void {
		this.vgAPI = this.webcast.video.vgAPI = event.vgAPI;
		this.cdr.detectChanges();
		this.onPlayerReady.emit(event);
	}

	public onFullscreenToggled(isFullscreen: boolean): void {
		this.webcast.layout.isFullscreenVideo = isFullscreen;
	}

	public onTimerEnd(): void {
		this.disposePlayer = true;
	}

	public onPlayerSizeChanged(playerSize: { height: number, width: number }): void {
		this.playerSizeSubject$.next({ height: playerSize.height, width: playerSize.width });
	}

	private subscribeViewerIdOverlay(): void {
		this.viewerIdOverlayChanged$ = this.webcast.webcast$
			.pipe(
				pluck('videoOverlays'),
				map(overlays => overlays?.find(overlay => overlay.type === VIDEO_OVERLAY_TYPE.VIEWER_ID)),
				distinctUntilChanged()
			);

		this.videoContainerSizeChanged$ = this.playerSizeSubject$.asObservable()
			.pipe(
				distinctUntilChanged(),
				delay(200)
			);

		this.subscription.add(
			combineLatest([
				this.viewerIdOverlayChanged$,
				this.videoContainerSizeChanged$
			])
				.subscribe(response => {
					const overlay = response[0];
					const size = response[1];

					if (overlay && size) {
						refreshViewerIdOverlay(this.webcast, this.prevPlayerSize, size);
						this.prevPlayerSize = size;
					}
				})
		);
	}

}
